// src/components/ProfessionalDashboard.tsx
import React, { useState, useEffect, useMemo } from "react";
import "../style/css/DashboardStyles.css";
import api from "../api.js";
import GeneralStats from "./tabs/GeneralStats";
import AppointmentsStats from "./tabs/AppointmentsStats";
import PatientsStats from "./tabs/PatientsStats";
import ServicesStats from "./tabs/ServicesStats";

// 1. Define possible tab and filter names
type TabName = "general" | "appointments" | "patients" | "services";

interface Tab {
  name: TabName;
  label: string;
  filterId: string;
  component: React.FC<any>;
}

interface GeneralData {
  totalCompletedAppointments: number;
  totalRevenue: number;
  cancellationNoShowRate: number;
  newPatients: number;
  averageRevenuePerVisit: number;
  appointmentsByService: Array<{
    serviceName: string;
    appointmentCount: number;
    percentage: number;
  }>;
  appointmentsByLocation: Array<{
    locationName: string;
    appointmentCount: number;
  }>;
  keyMetrics: {
    patientGrowth: number; // percentage
    // Add other metrics as needed
  };
}

interface AppointmentsData {
  statusCounts: {
    total: number;
    confirmed: number;
    pending: number;
    cancelled: number;
    completed: number;
    noshow: number;
  };
  appointmentsByHour: Array<{ hour: string; count: number }>;
  appointmentsByDay: Array<{ day: string; count: number }>;
  summary: {
    cancellationRate: number;
    completionRate: number;
    noShowRate: number;
    pendingRate: number;
  };
}

// Add PatientsData interface
interface AgeBucket {
  ageGroup: string;
  count: number;
}

interface TopActivePatient {
  patientName: string;
  appointmentCount: number;
  patientID?: string;
}

interface PatientsData {
  totalPatientsInPeriod: number;
  ageDistribution: AgeBucket[];
  patientActivityMetrics: {
    topActivePatients: TopActivePatient[];
    firstTimeVsRecurrent: { firstTime: number; recurrent: number };
  };
}

// Add ServicesData interface based on the previous structure
interface ServicesData {
  inventory: {
    totalActiveServices: number;
    totalServiceTypes: number;
    servicesByType: { type: string; count: number; percentage: number }[];
    uncategorizedServices: number;
    uncategorizedChange: number;
  };
  pricing: {
    averagePrice: number;
    priceRange: { min: number; max: number };
    averagePriceByType: { type: string; averagePrice: number }[];
    averageDuration: number;
    shortestServices: { service: string; duration: number }[];
    longestServices: { service: string; duration: number }[];
  };
  location: {
    totalOffices: number;
    usageRate: number;
    usageChange: number;
    officeUsage: { office: string; usageRate: number; appointments: number }[];
  };
  performance: {
    topSellingServices: { service: string; sales: number; revenue: number }[];
    highestRevenueTypes: { type: string; revenue: number; growth: number }[];
    leastProfitableServices: {
      service: string;
      revenue: number;
      cost: number;
      profit: number;
    }[];
    busiestOffice: {
      office: string;
      appointments: number;
      utilization: number;
    };
  };
}

// 2. Tab configuration
const tabs: Tab[] = [
  {
    name: "general",
    label: "Generales",
    filterId: "stats-general-filters",
    component: GeneralStats,
  },
  {
    name: "appointments",
    label: "Citas",
    filterId: "stats-appointments-filters",
    component: AppointmentsStats,
  },
  {
    name: "patients",
    label: "Pacientes",
    filterId: "stats-patients-filters",
    component: PatientsStats,
  },
  {
    name: "services",
    label: "Servicios",
    filterId: "stats-services-filters",
    component: ServicesStats,
  },
];

// Helper to set default dates
const setDefaultDates = (): { startDate: string; endDate: string } => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 1);

  const formatDate = (date: Date) => date.toISOString().split("T")[0];

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
};

interface DateRangeAlertProps {
  message: string | null;
  onClose: () => void;
}

const DateRangeAlert: React.FC<DateRangeAlertProps> = ({
  message,
  onClose,
}) => {
  if (!message) return null;

  return (
    <div className="custom-alert-overlay" onClick={onClose}>
      <div className="custom-alert-box" onClick={(e) => e.stopPropagation()}>
        <div className="alert-icon">⚠️</div>
        <p className="alert-message">{message}</p>
        <button className="alert-close-btn" onClick={onClose}>
          &times;
        </button>
      </div>
    </div>
  );
};

// 3. Main Dashboard Component
const ProfessionalDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabName>("general");
  const [dateRange, setDateRange] = useState(setDefaultDates());
  const [appointmentsData, setAppointmentsData] =
    useState<AppointmentsData | null>(null);
  const [generalData, setGeneralData] = useState<GeneralData | null>(null);
  const [patientsData, setPatientsData] = useState<PatientsData | null>(null);
  const [servicesData, setServicesData] = useState<ServicesData | null>(null); // NEW: Services data state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>("UTC");
  const [dateAlertMessage, setDateAlertMessage] = useState<string | null>(null);

  // Placeholder state for other filters
  const [generalFilters, setGeneralFilters] = useState({
    timePeriod: "last-month",
    location: "all",
  });

  // Get the browser's timezone once when the component mounts
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezone(tz);
    } catch (e) {}
  }, []);

  useEffect(() => {
    // Include userTimezone as a dependency to ensure API calls use the correct timezone
    if (activeTab === "appointments") {
      fetchAppointmentsData();
    } else if (activeTab === "patients") {
      fetchPatientsData();
    } else if (activeTab === "general") {
      fetchGeneralData();
    } else if (activeTab === "services") {
      // NEW: Fetch services data
      fetchServicesData();
    }
  }, [activeTab, dateRange, userTimezone]);

  const fetchGeneralData = async () => {
    setLoading(true);
    setError(null);
    try {
      const encodedTimezone = encodeURIComponent(userTimezone);
      const response = await api.get(
        `appointments/general_stats/?start=${dateRange.startDate}&end=${dateRange.endDate}&timezone=${encodedTimezone}`,
      );
      const data: GeneralData = response.data;
      setGeneralData(data);
    } catch (err) {
      if (err instanceof Error && err.message === "FORBIDDEN") {
        setError("FORBIDDEN");
      } else {
        setError("Failed to fetch general statistics data");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointmentsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const encodedTimezone = encodeURIComponent(userTimezone);
      const response = await api.get(
        `appointments/appointments_stats/?start=${dateRange.startDate}&end=${dateRange.endDate}&timezone=${encodedTimezone}`,
      );
      const data: AppointmentsData = response.data;
      setAppointmentsData(data);
    } catch (err) {
      if (err instanceof Error && err.message === "FORBIDDEN") {
        setError("FORBIDDEN");
      } else {
        setError("Failed to fetch appointments data");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const encodedTimezone = encodeURIComponent(userTimezone);
      const response = await api.get(
        `patients/patients_stats/?start=${dateRange.startDate}&end=${dateRange.endDate}&timezone=${encodedTimezone}`,
      );
      const data: PatientsData = response.data;
      setPatientsData(data);
    } catch (err) {
      if (err instanceof Error && err.message === "FORBIDDEN") {
        setError("FORBIDDEN");
      } else {
        setError("Failed to fetch patients data");
      }
    } finally {
      setLoading(false);
    }
  };

  // NEW: Fetch services data function
  const fetchServicesData = async () => {
    setLoading(true);
    setError(null);
    try {
      const encodedTimezone = encodeURIComponent(userTimezone);
      const response = await api.get(
        `services/services_stats/?start=${dateRange.startDate}&end=${dateRange.endDate}&timezone=${encodedTimezone}`,
      );
      const data: ServicesData = response.data;
      setServicesData(data);
    } catch (err) {
      if (err instanceof Error && err.message === "FORBIDDEN") {
        setError("FORBIDDEN");
      } else {
        setError("Failed to fetch services data");
      }
    } finally {
      setLoading(false);
    }
  };

  const ActiveComponent = useMemo(() => {
    const TabComponent =
      tabs.find((t) => t.name === activeTab)?.component || GeneralStats;

    if (activeTab === "appointments") {
      return () => (
        <TabComponent
          data={appointmentsData}
          loading={loading}
          error={error}
          dateRange={dateRange}
          onRetry={fetchAppointmentsData}
        />
      );
    } else if (activeTab === "patients") {
      return () => (
        <TabComponent
          data={patientsData}
          loading={loading}
          error={error}
          dateRange={dateRange}
          onRetry={fetchPatientsData}
        />
      );
    } else if (activeTab === "general") {
      return () => (
        <TabComponent
          data={generalData}
          loading={loading}
          error={error}
          dateRange={dateRange}
          onRetry={fetchGeneralData}
        />
      );
    } else if (activeTab === "services") {
      // NEW: Services tab
      return () => (
        <TabComponent
          data={servicesData}
          loading={loading}
          error={error}
          dateRange={dateRange}
          onRetry={fetchServicesData}
        />
      );
    }

    return () => <TabComponent />;
  }, [
    activeTab,
    appointmentsData,
    patientsData,
    generalData,
    servicesData, // NEW: Add servicesData to dependencies
    loading,
    error,
    dateRange,
  ]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    let newStartDate = dateRange.startDate;
    let newEndDate = dateRange.endDate;

    if (id.includes("start-date")) {
      newStartDate = value;
    } else if (id.includes("end-date")) {
      newEndDate = value;
    }

    // Date Validation: End Date cannot be before Start Date
    if (new Date(newStartDate) > new Date(newEndDate)) {
      if (id.includes("end-date")) {
        setDateAlertMessage(
          "La Fecha de Fin no puede ser anterior a la Fecha de Inicio.",
        );
        return;
      } else if (id.includes("start-date")) {
        setDateRange({ startDate: newStartDate, endDate: newStartDate });
        return;
      }
    }

    setDateRange({ startDate: newStartDate, endDate: newEndDate });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.target;
    if (id === "stats-time-filter") {
      setGeneralFilters((prev) => ({ ...prev, timePeriod: value }));
    } else if (id === "stats-location-filter") {
      setGeneralFilters((prev) => ({ ...prev, location: value }));
    }
  };

  return (
    <div className="container">
      <div className="stats-tabs">
        {tabs.map((tab) => (
          <div
            key={tab.name}
            className={`stats-tab ${activeTab === tab.name ? "active" : ""}`}
            onClick={() => setActiveTab(tab.name)}
            data-tab={tab.name}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* FILTERS SECTION */}

      {/* GENERAL Filters */}
      {activeTab == "general" ? (
        <div className="stats-dashboard-header">
          <h2>Panel de Estadísticas del Profesional</h2>
        </div>
      ) : (
        <div></div>
      )}
      {activeTab == "appointments" ? (
        <div className="stats-dashboard-header">
          <h2>Estadísticas Detalladas de Citas</h2>
        </div>
      ) : (
        <div></div>
      )}
      {activeTab == "patients" ? (
        <div className="stats-dashboard-header">
          <h2>Estadísticas de Pacientes</h2>
          <p>
            Métricas de adquisición, retención y demografía de la base de
            pacientes.
          </p>
        </div>
      ) : (
        <div></div>
      )}
      {activeTab == "services" ? (
        <div className="stats-dashboard-header">
          <h2>Estadísticas de Servicios Ofrecidos</h2>
          <p>Rendimiento e ingresos generados por cada tipo de servicio.</p>
        </div>
      ) : (
        <div></div>
      )}

      <div
        className={`stats-filters ${activeTab !== "general" ? "hidden" : ""}`}
        id="stats-general-filters"
      >
        <div className="stats-filter-group">
          <label htmlFor="stats-general-start-date">Fecha de Inicio</label>
          <input
            type="date"
            id="stats-general-start-date"
            className="stats-date-input"
            value={dateRange.startDate}
            onChange={handleDateChange}
            max={dateRange.endDate}
          />
        </div>
        <div className="stats-filter-group">
          <label htmlFor="stats-general-end-date">Fecha de Fin</label>
          <input
            type="date"
            id="stats-general-end-date"
            className="stats-date-input"
            value={dateRange.endDate}
            onChange={handleDateChange}
            min={dateRange.startDate}
          />
        </div>
      </div>

      {/* APPOINTMENTS Filters */}
      <div
        className={`stats-filters ${
          activeTab !== "appointments" ? "hidden" : ""
        }`}
        id="stats-appointments-filters"
      >
        <div className="stats-filter-group">
          <label htmlFor="stats-appointments-start-date">Fecha de Inicio</label>
          <input
            type="date"
            id="stats-appointments-start-date"
            className="stats-date-input"
            value={dateRange.startDate}
            onChange={handleDateChange}
            max={dateRange.endDate}
          />
        </div>
        <div className="stats-filter-group">
          <label htmlFor="stats-appointments-end-date">Fecha de Fin</label>
          <input
            type="date"
            id="stats-appointments-end-date"
            className="stats-date-input"
            value={dateRange.endDate}
            onChange={handleDateChange}
            min={dateRange.startDate}
          />
        </div>
      </div>

      {/* PATIENTS Filters */}
      <div
        className={`stats-filters ${activeTab !== "patients" ? "hidden" : ""}`}
        id="stats-patients-filters"
      >
        <div className="stats-filter-group">
          <label htmlFor="stats-patients-start-date">Fecha de Inicio</label>
          <input
            type="date"
            id="stats-patients-start-date"
            className="stats-date-input"
            value={dateRange.startDate}
            onChange={handleDateChange}
            max={dateRange.endDate}
          />
        </div>
        <div className="stats-filter-group">
          <label htmlFor="stats-patients-end-date">Fecha de Fin</label>
          <input
            type="date"
            id="stats-patients-end-date"
            className="stats-date-input"
            value={dateRange.endDate}
            onChange={handleDateChange}
            min={dateRange.startDate}
          />
        </div>
      </div>

      {/* SERVICES Filters */}
      <div
        className={`stats-filters ${activeTab !== "services" ? "hidden" : ""}`}
        id="stats-services-filters"
      >
        <div className="stats-filter-group">
          <label htmlFor="stats-services-start-date">Fecha de Inicio</label>
          <input
            type="date"
            id="stats-services-start-date"
            className="stats-date-input"
            value={dateRange.startDate}
            onChange={handleDateChange}
            max={dateRange.endDate}
          />
        </div>
        <div className="stats-filter-group">
          <label htmlFor="stats-services-end-date">Fecha de Fin</label>
          <input
            type="date"
            id="stats-services-end-date"
            className="stats-date-input"
            value={dateRange.endDate}
            onChange={handleDateChange}
            min={dateRange.startDate}
          />
        </div>
      </div>

      {/* ACTIVE TAB CONTENT */}
      <ActiveComponent />

      {/* Alert for date validation */}
      <DateRangeAlert
        message={dateAlertMessage}
        onClose={() => setDateAlertMessage(null)}
      />
    </div>
  );
};

export default ProfessionalDashboard;
